import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../utils/api";

// Restaurant's own email account (SMTP) for sending login codes by email.
// Choose a provider to fill the server/port, enter the login, save, then
// "Send test email". The password is stored encrypted and never shown again.
const PRESETS = [
  { id: "gmail", label: "Gmail / Google Workspace", host: "smtp.gmail.com", port: 587, secure: false,
    hint: "Use an App password (Google Account → Security → App passwords), not your normal password." },
  { id: "zoho", label: "Zoho Mail", host: "smtp.zoho.com", port: 587, secure: false,
    hint: "India accounts use smtp.zoho.in." },
  { id: "outlook", label: "Outlook / Microsoft 365", host: "smtp.office365.com", port: 587, secure: false,
    hint: "SMTP AUTH must be enabled for the mailbox." },
  { id: "ses", label: "Amazon SES", host: "email-smtp.me-central-1.amazonaws.com", port: 587, secure: false,
    hint: "Change the region in the server name if needed. Use SES SMTP credentials." },
  { id: "brevo", label: "Brevo", host: "smtp-relay.brevo.com", port: 587, secure: false,
    hint: "Username is your Brevo login email; password is an SMTP key." },
  { id: "custom", label: "Other (custom)", host: "", port: 587, secure: false, hint: "" },
];

const inputClass =
  "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200";
const labelClass = "block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5";
const btn =
  "px-4 py-2.5 rounded-xl text-sm font-semibold active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed";

const SOURCE_BADGE = {
  restaurant: { text: "Active", cls: "bg-green-100 text-green-700" },
  env: { text: "Using server default", cls: "bg-blue-100 text-blue-700" },
  none: { text: "Not set up", cls: "bg-gray-100 text-gray-600" },
};

const presetFor = (host) => PRESETS.find((p) => p.host && p.host === host)?.id || (host ? "custom" : "gmail");

const EmailSettingsSection = () => {
  const [form, setForm] = useState({ host: "", port: 587, secure: false, user: "", password: "", from: "" });
  const [preset, setPreset] = useState("gmail");
  const [hasPassword, setHasPassword] = useState(false);
  const [activeSource, setActiveSource] = useState("none");
  const [testTo, setTestTo] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const applySettings = (s) => {
    setForm({ host: s.host, port: s.port, secure: s.secure, user: s.user, password: "", from: s.from });
    setPreset(presetFor(s.host));
    setHasPassword(s.hasPassword);
    setActiveSource(s.activeSource);
    setTestTo((t) => t || s.user || "");
  };

  useEffect(() => {
    api
      .get("/settings/email")
      .then((res) => applySettings(res.data.settings))
      .catch((err) => {
        if (err?.response?.status !== 403) toast.error("Failed to load email settings");
      })
      .finally(() => setIsLoading(false));
  }, []);

  const choosePreset = (id) => {
    setPreset(id);
    const p = PRESETS.find((x) => x.id === id);
    if (p && p.id !== "custom") setForm((f) => ({ ...f, host: p.host, port: p.port, secure: p.secure }));
  };

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    setIsSaving(true);
    try {
      const res = await api.put("/settings/email", { ...form, port: Number(form.port) });
      applySettings(res.data.settings);
      toast.success("Email settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save email settings");
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the restaurant's email settings?")) return;
    setIsSaving(true);
    try {
      const res = await api.put("/settings/email", { clear: true });
      applySettings(res.data.settings);
      toast.info("Email settings removed");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove email settings");
    } finally {
      setIsSaving(false);
    }
  };

  const sendTest = async () => {
    setIsTesting(true);
    try {
      const res = await api.post("/settings/email/test", { to: testTo });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Test email failed", { autoClose: 8000 });
    } finally {
      setIsTesting(false);
    }
  };

  const badge = SOURCE_BADGE[activeSource] || SOURCE_BADGE.none;
  const presetHint = PRESETS.find((p) => p.id === preset)?.hint;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-4">
        <div className="w-7 h-7 rounded-lg bg-black flex items-center justify-center text-white flex-shrink-0">
          <span className="text-xs font-semibold">5</span>
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-gray-900">Email (SMTP)</h2>
          <p className="text-xs text-gray-500">
            Your email account for sending customer login codes by email.
          </p>
        </div>
        {!isLoading && (
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badge.cls}`}>{badge.text}</span>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Email provider</label>
            <select value={preset} onChange={(e) => choosePreset(e.target.value)} className={inputClass}>
              {PRESETS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            {presetHint && <p className="text-xs text-gray-500 mt-1">{presetHint}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
            <div>
              <label className={labelClass}>SMTP server</label>
              <input className={inputClass} value={form.host} onChange={set("host")} placeholder="smtp.example.com" />
            </div>
            <div>
              <label className={labelClass}>Port</label>
              <input className={inputClass} value={form.port} onChange={set("port")} inputMode="numeric" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.secure} onChange={set("secure")} />
            Use SSL (only for port 465)
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Username</label>
              <input className={inputClass} value={form.user} onChange={set("user")} placeholder="no-reply@yourdomain.com" autoComplete="off" />
            </div>
            <div>
              <label className={labelClass}>Password</label>
              <input
                type="password"
                className={inputClass}
                value={form.password}
                onChange={set("password")}
                placeholder={hasPassword ? "•••••••• saved – leave blank to keep" : "App password / SMTP key"}
                autoComplete="new-password"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>From (optional)</label>
            <input className={inputClass} value={form.from} onChange={set("from")} placeholder='Kairali <no-reply@yourdomain.com>' />
            <p className="text-xs text-gray-500 mt-1">Leave blank to send from the username. Should be the same account or a verified domain.</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={save} disabled={isSaving} className={`${btn} bg-blue-600 hover:bg-blue-700 text-white`}>
              {isSaving ? "Saving…" : "Save email settings"}
            </button>
            {hasPassword && (
              <button type="button" onClick={remove} disabled={isSaving} className={`${btn} bg-gray-100 hover:bg-gray-200 text-gray-800`}>
                Remove
              </button>
            )}
          </div>

          {activeSource !== "none" && (
            <div className="pt-4 border-t border-gray-100">
              <label className={labelClass}>Send a test email</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input className={inputClass} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" />
                <button type="button" onClick={sendTest} disabled={isTesting || !testTo} className={`${btn} bg-green-600 hover:bg-green-700 text-white whitespace-nowrap`}>
                  {isTesting ? "Sending…" : "Send test"}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Uses the saved settings. Save first if you changed anything.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailSettingsSection;
